import type { ReactNode } from 'react';

import { Modal } from './modal';
import { Button } from './ui/button';

const LEFT_ALIGNED_HEADER = '[&_[data-slot=dialog-header]]:text-left';

const FILLED_DESTRUCTIVE =
  'bg-destructive text-white hover:bg-destructive/90 hover:text-white dark:text-background dark:hover:text-background';

interface ConfirmDialogProps {
  title: string;
  description: string;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /**
   * With `destructive`, draws the confirm button filled rather than outlined, so it does not read
   * as the secondary action beside Cancel. Dark mode takes dark text: the dark destructive token
   * is a light red that white text does not read on.
   */
  filled?: boolean;
  /** Keeps the header left-aligned at every width; by default it is centred on a phone. */
  leftAligned?: boolean;
  pending?: boolean;
  /** Holds the confirm button off until the dialog's own input is valid; Cancel stays available. */
  confirmDisabled?: boolean;
  children?: ReactNode;
}

/** The one shape every destructive confirmation takes, so no screen improvises its own. */
export function ConfirmDialog({
  title,
  description,
  open,
  onClose,
  onConfirm,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  filled = false,
  leftAligned = false,
  pending = false,
  confirmDisabled = false,
  children,
}: ConfirmDialogProps) {
  return (
    <Modal
      title={title}
      description={description}
      open={open}
      onClose={onClose}
      className={leftAligned ? LEFT_ALIGNED_HEADER : undefined}
    >
      {children}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
          {cancelLabel}
        </Button>
        <Button
          type="button"
          variant={destructive ? 'destructive' : 'default'}
          className={destructive && filled ? FILLED_DESTRUCTIVE : undefined}
          onClick={onConfirm}
          disabled={pending || confirmDisabled}
        >
          {pending ? 'Working…' : confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
